import { useState, useEffect, useCallback, MutableRefObject } from 'react';
import useUnmount from './useUnmount';
import { Maybe } from '@ids/types/utility';

interface UseInViewParams<T> {
    containerRef: MutableRefObject<Maybe<T>>;
    options?: IntersectionObserverInit;
    /**
     * If set to `true` (default) it will unobserve the entry as soon as it intersects.
     *
     * If set to `false` it will keep observing the entry even after it was intersected.
     *
     */
    triggerOnce?: boolean;
    /**
     * A callback that triggers once, only when defined and the entry has intersected.
     */
    onVisibleCallback?: () => void;
}

export default function useInView<T extends Element>({
    containerRef,
    options = {
        root: null,
        rootMargin: '0px',
        threshold: 0,
    },
    triggerOnce = true,
    onVisibleCallback,
}: UseInViewParams<T>) {
    const [isVisible, setIsVisible] = useState(false);
    const [onVisibleCallbackTriggered, setOnVisibleCallbackTriggered] = useState(false);

    useUnmount(
        useCallback(() => {
            setIsVisible(false);
            setOnVisibleCallbackTriggered(false);
        }, [])
    );

    const callbackFn = useCallback((entries: IntersectionObserverEntry[]) => {
        const [entry] = entries;

        setIsVisible(entry.isIntersecting);
    }, []);

    useEffect(() => {
        const { current } = containerRef;
        const observer = new IntersectionObserver(callbackFn, options);

        if (current) {
            observer.observe(current);

            if (isVisible && triggerOnce) {
                observer.unobserve(current);
            }
        }

        return () => {
            if (current) {
                observer.unobserve(current);
            }
        };
    }, [callbackFn, containerRef, isVisible, options, triggerOnce]);

    useEffect(() => {
        if (isVisible && onVisibleCallback && !onVisibleCallbackTriggered) {
            onVisibleCallback();
            setOnVisibleCallbackTriggered(true);
        }
    }, [isVisible, onVisibleCallback, onVisibleCallbackTriggered]);

    return isVisible;
}
